import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import Icons from '../components/Icons';
import BulkActionBar from '../components/inventory/BulkActionBar';
import WorkspaceKpis from '../components/workspace/WorkspaceKpis';
import SearchableSelect from '../components/ui/SearchableSelect';
import { useStudentStore } from '../store/StudentStore';
import { useSettings } from '../store/SettingsContext';
import ProgrammeService from '../services/ProgrammeService';
import ActivityService from '../services/ActivityService';
import StaffService from '../services/StaffService';
import { formatDate } from '../utils/dateFormat';

const buildProgrammeStats = (students, programmes) => {
  const programmeMap = new Map();
  programmes.forEach((programme) => {
    programmeMap.set(programme.name, {
      ...programme,
      type: 'programme',
      attendanceRates: [],
      enrolled: 0,
      totalSessions: 0
    });
  });

  students.forEach((student) => {
    (student.enrolments || []).forEach((enrol) => {
      if (!programmeMap.has(enrol.programme)) return;
      const entry = programmeMap.get(enrol.programme);
      entry.enrolled += 1;
      entry.attendanceRates.push(student.attendance?.rate || 0);
    });
    (student.attendance?.logs || []).forEach((log) => {
      if (!programmeMap.has(log.programme)) return;
      programmeMap.get(log.programme).totalSessions += 1;
    });
  });

  return Array.from(programmeMap.values()).map((entry) => {
    const avgAttendance = entry.attendanceRates.length
      ? Math.round(entry.attendanceRates.reduce((sum, value) => sum + value, 0) / entry.attendanceRates.length)
      : 0;
    const capacity = entry.capacity || 0;
      return {
        ...entry,
        avgAttendance,
        capacity,
        utilisation: capacity ? Math.round((entry.enrolled / capacity) * 100) : 0,
        staff: entry.staff || 'Unassigned',
        schedule: entry.day || (entry.days ? entry.days.join(', ') : 'TBD'),
        lessonPlanCount: entry.lessonPlans?.length || 0,
        lessonPlans: entry.lessonPlans || [],
        documentCount: entry.documents?.length || 0,
        documents: entry.documents || []
      };
    });
  };

const buildActivityStats = (activities) => {
  return activities.map((activity) => {
    const capacity = activity.capacity || 0;
    const enrolled = activity.enrolled || 0;
    return {
      ...activity,
      type: 'activity',
      avgAttendance: capacity ? Math.round((enrolled / capacity) * 100) : 0,
      capacity,
      enrolled,
      staff: activity.lead || 'Unassigned',
      schedule: activity.date ? `${formatDate(activity.date)} · ${activity.time || ''}` : activity.time || 'TBD',
      lessonPlanCount: activity.lessonPlans?.length || 0,
      lessonPlans: activity.lessonPlans || [],
      documentCount: activity.documents?.length || 0,
      documents: activity.documents || []
    };
  });
};

const safeList = (service) => {
  if (service && typeof service.list === 'function') return service.list();
  return [];
};

export default function ProgrammesPage() {
  const { students } = useStudentStore();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const attendanceThreshold = settings.attendanceThreshold || 70;
  const programmeService = useMemo(() => new ProgrammeService(), []);
  const activityService = useMemo(() => new ActivityService(), []);
  const staffService = useMemo(() => new StaffService(), []);
  const [programmes, setProgrammes] = useState(() => safeList(programmeService));
  const [activities, setActivities] = useState(() => safeList(activityService));
  const staffList = useMemo(() => safeList(staffService), [staffService]);

  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({ type: 'all' });
  const [activeKpi, setActiveKpi] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [activeItemId, setActiveItemId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState('');
  const [assignSelectionIds, setAssignSelectionIds] = useState([]);
  const [actionFeedback, setActionFeedback] = useState('');

  const programmeStats = useMemo(() => buildProgrammeStats(students, programmes), [students, programmes]);
  const activityStats = useMemo(() => buildActivityStats(activities), [activities]);

  const items = useMemo(() => {
    const merged = [...programmeStats, ...activityStats].filter((item) => {
      if (filters.type !== 'all' && item.type !== filters.type) return false;
      if (!searchQuery) return true;
      return `${item.name} ${item.staff}`.toLowerCase().includes(searchQuery.toLowerCase());
    });
    return merged.sort((a, b) => {
      const aRisk = a.avgAttendance > 0 && a.avgAttendance < attendanceThreshold ? 1 : 0;
      const bRisk = b.avgAttendance > 0 && b.avgAttendance < attendanceThreshold ? 1 : 0;
      if (bRisk !== aRisk) return bRisk - aRisk;
      return a.name.localeCompare(b.name);
    });
  }, [programmeStats, activityStats, filters, searchQuery, attendanceThreshold]);

  const kpiItems = useMemo(() => {
    const activeProgrammes = programmeStats.length;
    const lowAttendance = programmeStats.filter((p) => p.avgAttendance > 0 && p.avgAttendance < attendanceThreshold).length;
    const safeguardingLinked = activityStats.filter((a) => a.safeguarding?.consentRequired || !a.safeguarding?.riskAssessmentComplete).length;
    const upcomingSessions = activityStats.filter((a) => a.date && new Date(a.date) >= new Date()).length;
    return [
      { id: 'active', label: 'Active programmes', value: activeProgrammes },
      { id: 'low', label: 'Low attendance', value: lowAttendance, badge: { label: 'Watch', variant: 'warning' } },
      { id: 'safeguarding', label: 'Safeguarding linked', value: safeguardingLinked },
      { id: 'upcoming', label: 'Upcoming sessions', value: upcomingSessions }
    ];
  }, [programmeStats, activityStats, attendanceThreshold]);

  const filteredItems = useMemo(() => {
    if (!activeKpi) return items;
    if (activeKpi === 'low') return items.filter((item) => item.avgAttendance > 0 && item.avgAttendance < attendanceThreshold);
    if (activeKpi === 'safeguarding') return items.filter((item) => item.safeguarding?.consentRequired || !item.safeguarding?.riskAssessmentComplete);
    if (activeKpi === 'upcoming') return items.filter((item) => item.date && new Date(item.date) >= new Date());
    return items;
  }, [items, activeKpi, attendanceThreshold]);

  const activeItem = items.find((item) => item.id === activeItemId);

  const toggleSelection = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (value) => {
    if (!value) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(filteredItems.map((item) => item.id)));
  };

  const openDrawer = (id) => {
    setActiveItemId(id);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setActiveItemId(null);
    setDrawerOpen(false);
  };

  const bulkActions = useMemo(() => {
    const downloadCsv = (rows, filename) => {
      const csv = rows
        .map((row) => row.map((value) => `"${`${value ?? ''}`.replace(/"/g, '""')}"`).join(','))
        .join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    };
    return [
      {
        id: 'assign-staff',
        label: 'Assign staff',
        onClick: () => {
          setAssignSelectionIds(Array.from(selectedIds));
          setAssignModalOpen(true);
        },
        disabled: selectedIds.size === 0
      },
      {
        id: 'export-attendance',
        label: 'Export attendance',
        onClick: () => {
          const selectedItems = items.filter((item) => selectedIds.has(item.id));
          if (selectedItems.length === 0) return;
          const rows = [
            ['Name', 'Type', 'Schedule', 'Attendance %', 'Staff', 'Enrolled', 'Capacity'],
            ...selectedItems.map((item) => [
              item.name,
              item.type,
              item.schedule,
              item.avgAttendance,
              item.staff || 'Unassigned',
              item.enrolled || 0,
              item.capacity || 0
            ])
          ];
          downloadCsv(rows, `programmes-attendance-${new Date().toISOString().slice(0, 10)}.csv`);
          setActionFeedback(`Exported ${selectedItems.length} selected item(s).`);
        },
        disabled: selectedIds.size === 0
      },
      {
        id: 'archive-programme',
        label: 'Archive programme',
        onClick: () => {
          const targetIds = new Set(Array.from(selectedIds));
          const matchedProgrammes = programmes.filter((programme) => targetIds.has(programme.id));
          matchedProgrammes
            .forEach((programme) => programmeService.update({ ...programme, archived: true }));
          const nextProgrammes = programmes.map((programme) =>
            targetIds.has(programme.id) ? { ...programme, archived: true } : programme
          );
          setProgrammes(nextProgrammes);
          setSelectedIds(new Set());
          setActionFeedback(`Archived ${matchedProgrammes.length} programme(s).`);
        },
        disabled: selectedIds.size === 0 || programmes.filter((programme) => selectedIds.has(programme.id)).length === 0
      }
    ];
  }, [selectedIds, programmes, programmeService, items]);

  const applyAssignStaff = () => {
    if (!assignTarget) return;
    const targetIds = assignSelectionIds.length
      ? new Set(assignSelectionIds)
      : new Set(selectedIds.size ? Array.from(selectedIds) : activeItem ? [activeItem.id] : []);
    if (!targetIds.size) return;

    const nextProgrammes = programmes.map((programme) => {
      if (!targetIds.has(programme.id)) return programme;
      const updated = { ...programme, staff: assignTarget };
      programmeService.update(updated);
      return updated;
    });
    const nextActivities = activities.map((activity) => {
      if (!targetIds.has(activity.id)) return activity;
      const updated = { ...activity, lead: assignTarget };
      activityService.update(updated);
      return updated;
    });

    setProgrammes(nextProgrammes);
    setActivities(nextActivities);
    setAssignModalOpen(false);
    setAssignTarget('');
    setAssignSelectionIds([]);
    setSelectedIds(new Set());
    setActionFeedback(`Assigned staff to ${targetIds.size} item(s).`);
  };

  return (
    <div className="space-y-6">
      <div className="sp-page-header">
        <div>
          <h2 className="sp-value" style={{ fontSize: '20px', fontWeight: 600 }}>Activities & Programmes Workspace</h2>
          <p className="sp-muted">Track participation, capacity, and safeguarding-linked sessions.</p>
        </div>
        <div className="sp-page-actions">
          <Button size="sm" onClick={() => navigate('/programmes/overview/new')}>Add programme</Button>
          <Button size="sm" variant="secondary" onClick={() => navigate('/activities/new')}>Add activity</Button>
        </div>
      </div>
      {actionFeedback && (
        <div className="sp-alert sp-alert--success text-sm flex items-center justify-between gap-3">
          <span>{actionFeedback}</span>
          <Button size="sm" variant="ghost" onClick={() => setActionFeedback('')}>Dismiss</Button>
        </div>
      )}

      <WorkspaceKpis items={kpiItems} activeKpi={activeKpi} onSelect={setActiveKpi} />

      <div className="sp-filter-bar">
        <div className="sp-filter-main">
          <div>
            <label className="sp-label">Search</label>
            <div className="sp-search mt-2">
              <Icons.Search />
              <input
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search programmes or activities"
              />
            </div>
          </div>
          <div className="sp-filter-controls">
            <div>
              <label className="sp-label">Type</label>
              <select
                className="sp-select mt-2"
                value={filters.type}
                onChange={(event) => setFilters((prev) => ({ ...prev, type: event.target.value }))}
              >
                <option value="all">All</option>
                <option value="programme">Programmes</option>
                <option value="activity">Activities</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="sp-workspace-grid">
        <div>
          <div className="sp-table sp-table--programmes-workspace">
            <div className="sp-row sp-row--header">
              <div>
                <input
                  type="checkbox"
                  checked={selectedIds.size > 0 && selectedIds.size === filteredItems.length}
                  onChange={(event) => toggleSelectAll(event.target.checked)}
                  aria-label="Select all programmes"
                />
              </div>
              <div>Programme</div>
              <div>Schedule</div>
              <div>Attendance %</div>
              <div>Staff assigned</div>
              <div>Capacity</div>
              <div>Actions</div>
            </div>
            {filteredItems.map((item) => (
              <div
                key={item.id}
                className={`sp-row sp-row--inventory ${item.avgAttendance > 0 && item.avgAttendance < attendanceThreshold ? 'sp-row--warning' : ''}`}
                onClick={() => openDrawer(item.id)}
              >
                <div onClick={(event) => event.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(item.id)}
                    onChange={() => toggleSelection(item.id)}
                    aria-label={`Select ${item.name}`}
                  />
                </div>
                <div>
                  <div className="sp-value" style={{ fontWeight: 600 }}>{item.name}</div>
                  <div className="text-xs sp-muted">{item.type === 'activity' ? 'Activity' : 'Programme'}</div>
                </div>
                <div>{item.schedule}</div>
                <div>{item.avgAttendance}%</div>
                <div>{item.staff || 'Unassigned'}</div>
                <div>{item.enrolled || 0}/{item.capacity || '—'}</div>
                <div>
                  <Button size="sm" variant="ghost" onClick={(event) => {
                    event.stopPropagation();
                    if (item.type === 'activity') navigate(`/activities/${item.id}/edit`);
                    else navigate(`/programmes/overview/${item.id}/edit`);
                  }}>
                    Edit
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <Card className={`sp-workspace-drawer ${drawerOpen ? 'is-open' : ''}`}>
          {!activeItem ? (
            <div className="sp-drawer-empty">Select a row to view details.</div>
          ) : (
            <div className="space-y-4">
              <div className="sp-drawer-header">
                <div>
                  <div className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>{activeItem.name}</div>
                  <div className="text-xs sp-muted">{activeItem.type === 'activity' ? 'Activity' : 'Programme'}</div>
                </div>
                <Button size="sm" variant="ghost" onClick={closeDrawer}>Close</Button>
              </div>
              <div className="sp-drawer-section">
                <div className="sp-drawer-meta">
                  <div>
                    <div className="sp-label">Attendance</div>
                    <div className="sp-value">{activeItem.avgAttendance}%</div>
                  </div>
                  <div>
                    <div className="sp-label">Capacity</div>
                    <div className="sp-value">{activeItem.enrolled || 0}/{activeItem.capacity || '—'}</div>
                  </div>
                  <div>
                    <div className="sp-label">Staff</div>
                    <div className="sp-value">{activeItem.staff || 'Unassigned'}</div>
                  </div>
                </div>
              </div>
              <div className="sp-drawer-section">
                <div className="sp-label">Schedule</div>
                <div className="text-sm sp-muted">{activeItem.schedule}</div>
              </div>
              <div className="sp-drawer-section">
                <div className="sp-label">Lesson plans</div>
                <div className="text-sm sp-muted">
                  {activeItem.lessonPlanCount ? `${activeItem.lessonPlanCount} plan(s) uploaded` : 'No lesson plans uploaded'}
                </div>
                {activeItem.lessonPlans?.length > 0 && (
                  <div className="mt-2 space-y-2">
                    {activeItem.lessonPlans.slice(0, 3).map((plan) => (
                      <div key={plan.id} className="sp-panel sp-panel--muted p-3 text-xs">
                        <div className="sp-value" style={{ fontWeight: 600 }}>{plan.title || plan.fileName || 'Lesson plan'}</div>
                        <div className="text-xs sp-muted">
                          {plan.date ? formatDate(plan.date) : 'No date'}
                          {plan.archived ? ' · Archived' : ''}
                        </div>
                        {plan.linkUrl && (
                          <a className="sp-link text-xs mt-1 inline-block" href={plan.linkUrl} target="_blank" rel="noreferrer">
                            Open link
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="sp-drawer-section">
                <div className="sp-label">Course documents</div>
                <div className="text-sm sp-muted">
                  {activeItem.documentCount ? `${activeItem.documentCount} document(s)` : 'No documents uploaded'}
                </div>
                {activeItem.documents?.length > 0 && (
                  <div className="mt-2 space-y-2">
                    {activeItem.documents.slice(0, 3).map((doc) => (
                      <div key={doc.id || doc.name} className="sp-panel sp-panel--muted p-3 text-xs">
                        <div className="sp-value" style={{ fontWeight: 600 }}>{doc.name || doc.fileName || 'Document'}</div>
                        <div className="text-xs sp-muted">
                          {doc.archived ? 'Archived' : 'Active'}
                        </div>
                        {doc.linkUrl && (
                          <a className="sp-link text-xs mt-1 inline-block" href={doc.linkUrl} target="_blank" rel="noreferrer">
                            Open link
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="sp-drawer-actions">
                <Button
                  size="sm"
                  onClick={() => {
                    if (activeItem) {
                      setAssignSelectionIds([activeItem.id]);
                    }
                    setAssignModalOpen(true);
                  }}
                >
                  Assign staff
                </Button>
                <Button size="sm" variant="secondary" onClick={() => navigate('/attendance')}>View attendance</Button>
              </div>
            </div>
          )}
        </Card>
      </div>

      {drawerOpen && <div className="sp-drawer-overlay" onClick={closeDrawer} aria-hidden="true" />}

      <BulkActionBar selectedCount={selectedIds.size} actions={bulkActions} />

      <Modal
        isOpen={assignModalOpen}
        onClose={() => {
          setAssignModalOpen(false);
          setAssignSelectionIds([]);
          setAssignTarget('');
        }}
      >
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Assign staff</h3>
              <p className="text-sm sp-muted">Assign a lead to selected programmes or activities.</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setAssignModalOpen(false);
                setAssignSelectionIds([]);
              }}
            >
              Close
            </Button>
          </div>
          <SearchableSelect
            value={assignTarget}
            options={staffList.map((staff) => ({ value: staff.name, label: staff.name, meta: staff.role || '' }))}
            placeholder="Search staff"
            emptyLabel="No staff found."
            onChange={setAssignTarget}
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                setAssignModalOpen(false);
                setAssignSelectionIds([]);
              }}
            >
              Cancel
            </Button>
            <Button onClick={applyAssignStaff} disabled={!assignTarget}>Assign</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
